import {
  ChangeEvent,
  Context,
  FC,
  useCallback,
  useContext,
  useRef,
  useState
} from 'react';

import { Category, SearchSuggestion } from '@/__generated_graphql__/graphql';
import { Portal } from '@/components/Portal/Portal';
import { SearchBarDropdown } from '@/components/SearchBar/SearchBarDropdown';
import { SearchInput } from '@/components/SearchInput/SearchInput';
import { SearchIcon } from '@/components/ui/Icons/Icons';
import { Overlay } from '@/components/ui/Overlay/Overlay';
import {
  HEADER_AD_BUTTON_ID,
  HEADER_BOOKMARKS_BUTTON_ID
} from '@/constants/common';
import { useGetCategories } from '@/hooks/useGetCategories';
import { useGetSuggestions } from '@/hooks/useGetSuggestions';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useNavigate } from '@/hooks/useNavigate';
import { useSearchInputPosition } from '@/hooks/useSearchInputPosition';
import { useSearchRoute } from '@/hooks/useSearchRoute';
import { RequiredHeaderContextProps } from '@/types';
import { debounce } from '@/utils/helpers/debounce';

import * as S from './Header.styles';

type Props = {
  placeholder: string;
  context: Context<RequiredHeaderContextProps>;
};

export const HeaderSearchInput: FC<Props> = ({ placeholder, context }) => {
  const { onChangeQuery, query, formMethods, visible } = useContext(context);

  const isMobile = useMediaQuery('tablet');
  const isSmallDesktop = useMediaQuery('smallDesktop');

  const inputRef = useRef<HTMLInputElement | null>(null);

  const SEARCH = useSearchRoute();

  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState<string | Category>(query);
  const { deleteParams, setParams } = useNavigate();

  const { searchInputRef, dropdownRef } = useSearchInputPosition({
    isOpen,
    isBig: !isMobile,
    expandTargetId: !isSmallDesktop
      ? HEADER_BOOKMARKS_BUTTON_ID
      : HEADER_AD_BUTTON_ID,
    isHeaderFixed: true
  });

  const { data: suggestionsData, fetchMore } = useGetSuggestions({
    query: ''
  });

  const { data: categories } = useGetCategories();

  const handleDebounceQuery = useCallback(debounce(fetchMore, 100), []);

  const handleChangeSuggestions = (searchTerm: string) => {
    if (searchTerm) return handleDebounceQuery(searchTerm);
    fetchMore(searchTerm);
    handleDebounceQuery.cancel();
  };

  const handleSubmitChanges = (
    inputValue: string | Category = '',
    changeQuery: boolean = true
  ) => {
    const computedValue =
      typeof inputValue === 'object' ? inputValue.name : inputValue;

    setValue(inputValue);

    if (changeQuery) {
      handleChangeSuggestions(computedValue);
      onChangeQuery(computedValue);

      return computedValue
        ? setParams({ searchTerm: computedValue }, SEARCH)
        : deleteParams('searchTerm');
    }
  };

  const handleReset = () => {
    setIsOpen(false);
    handleSubmitChanges();

    if (typeof value === 'object') {
      deleteParams('categoryIds', SEARCH);
      if (formMethods) formMethods.setValue('categories', []);
    }
  };

  const handleClose = () => {
    if (inputRef.current) inputRef.current.blur();
    setValue(query);
    setIsOpen(false);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement> | string) => {
    let inputValue: string = '';
    if (typeof e === 'string') inputValue = e;
    else inputValue = e.target.value;
    setValue(inputValue);
    handleChangeSuggestions(inputValue);
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    const { searchTerm } = suggestion;
    handleSubmitChanges(searchTerm);
    setIsOpen(false);
  };

  const handleCategoryClick = (category: Category) => {
    setIsOpen(false);
    if (formMethods) formMethods.setValue('categories', [category]);
    handleSubmitChanges(category, false);
    handleChangeSuggestions('');
    setParams({ categoryIds: category.id, searchTerm: '' }, SEARCH);
  };

  const handleKeyDown = (input: HTMLInputElement | null) => {
    if (!input) return;
    if (typeof value !== 'object') handleSubmitChanges(input.value);
    setIsOpen(false);
    input.blur();
  };

  const handleBlurInput = () => {
    if (!inputRef.current) return;
    const { value: targetValue } = inputRef.current;

    if (typeof value === 'object') return handleSubmitChanges(value, false);

    handleSubmitChanges(targetValue);
    handleChangeSuggestions(targetValue);
  };

  const computedValue =
    typeof value === 'object' && 'name' in value ? value.name : value;

  const element = (
    <SearchInput
      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleKeyDown(inputRef.current);
      }}
      shouldExpand
      defaultValue={query}
      searchInputRef={searchInputRef}
      inputRef={inputRef}
      isBordered
      query={computedValue}
      isOpen={isOpen}
      placeholder={placeholder}
      onFocus={() => setIsOpen(true)}
      onBlur={handleBlurInput}
      onChange={handleChange}
      onReset={value || query ? handleReset : undefined}
    />
  );

  if (typeof visible !== 'object' && typeof visible !== 'undefined' && !visible)
    return null;

  return (
    <>
      {isOpen && <Overlay onClick={() => setIsOpen(false)} zIndex={5} />}

      {isMobile ? (
        <S.SearchButton onClick={() => setIsOpen(true)}>
          <SearchIcon />
        </S.SearchButton>
      ) : (
        element
      )}

      <Portal id="search-input-dropdown">
        <SearchBarDropdown
          onClear={handleReset}
          onClose={handleClose}
          onSuggestion={handleSuggestionClick}
          onCategory={handleCategoryClick}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          elRef={dropdownRef}
          searchSuggestion={suggestionsData}
          categories={categories}
          isOpen={isOpen}
          isModal={isMobile}
          query={value}
        />
      </Portal>
    </>
  );
};
